import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import HelpIcon from '@mui/icons-material/Help';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
    Button,
    ButtonGroup,
    Checkbox,
    ClickAwayListener,
    FormControl,
    FormControlLabel,
    Grid,
    Grow,
    InputLabel,
    MenuItem,
    MenuList,
    Popover,
    Popper,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import ColorConstants from '../../../defaults/ColorConstants';
import FieldNames from '../../../defaults/FieldNames.json';
import { setViewingActions, setViewingProjects } from '../../../redux/slices/menuSlice';
import ProjectListCard from '../../ProjectListCard';
import { MapLegend } from '../../popover/MapLegend/MapLegend';
import '../TreatmentActivityList/TreatmentActivityList.css';
import { setProjectSortByValue } from '../../../redux/slices/globalContextSlice';

const drawerWidth = 550;

const openedMixin = (theme, headerheight) => ({
    width: drawerWidth,
    top: headerheight + 'px',
    left: 'auto',
    backgroundColor: ColorConstants.AvatarBackgroundColor,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    overflowY: 'hidden',
});

const closedMixin = (theme, headerheight) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    backgroundColor: ColorConstants.AvatarBackgroundColor,
    top: headerheight + 'px',
    left: 'auto',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: theme.spacing(0, 1),
    height: '48px !important',
    backgroundColor: ColorConstants.WhiteAlien,
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open, headerheight }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme, headerheight),
            '& .MuiDrawer-paper': openedMixin(theme, headerheight),
        }),
        ...(!open && {
            ...closedMixin(theme, headerheight),
            '& .MuiDrawer-paper': closedMixin(theme, headerheight),
        }),
    })
);

export default function ProjectList({
    listOpen,
    setListOpen,
    userInfo,
    userRoleInfo,
    headerheight,
}) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isUserPostfire = useMemo(() => {
        return userInfo[FieldNames.userFields.isPostFire] === FieldNames.yesNo.yes;
    }, [userInfo]);
    const [actionOpen, setActionOpen] = useState(false);
    const anchorRef = useRef(null);
    const [countPopupAnchorEl, setCountPopupAnchorEl] = useState(null);
    const countPopupOpen = Boolean(countPopupAnchorEl);
    const countPopupID = countPopupOpen ? 'count-popover' : undefined;
    const projects = useSelector((state) => state.projects.visibleProjects);
    const [sortedProjects, setSortedProjects] = useState([]);
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [isCheckAll, setIsCheckAll] = useState(false);
    const [cardSize, setCardSize] = useState('short');

    const projectSortByValue = useSelector((state) => state.globalContext.projectSortByValue);

    const [projectListCount, setProjectListCount] = useState(50);
    const [tempProjectListCount, setTempProjectListCount] = useState(50);
    const areListCountsDifferent = tempProjectListCount !== projectListCount;
    const [tempSortByValue, setTempSortByValue] = useState(projectSortByValue);
    const currentMapExtent = useSelector((state) => state.map.currentMapExtent);

    const isViewingActions = useSelector((state) => state.menu.viewingActions);
    const isViewingProjects = useSelector((state) => state.menu.viewingProjects);
    const [projectActionSelection, setProjectActionSelection] = useState(
        isViewingActions ? 'Actions' : 'Projects'
    );

    useEffect(() => {
        setIsCheckAll(false);
        setSelectedProjects([]);
    }, [currentMapExtent]);

    useEffect(() => {
        try {
            let clonedProjects = _.cloneDeep(projects); // Cloned to avoid Reactjs readonly array issue
            let sortedFeatures = clonedProjects;
            if (clonedProjects?.length > 0) {
                if (projectSortByValue) {
                    const fieldSortByValue = projectSortByValue
                        .replace(' ASC', '')
                        .replace(' DESC', '');
                    sortedFeatures = clonedProjects.sort((a, b) => {
                        let firstItem = a[fieldSortByValue];
                        let secondItem = b[fieldSortByValue];
                        if (!firstItem) {
                            firstItem = '';
                        }
                        if (!secondItem) {
                            secondItem = '';
                        }
                        firstItem = firstItem ? firstItem.toLowerCase() : '';
                        secondItem = secondItem ? secondItem.toLowerCase() : '';
                        let sortRet = firstItem > secondItem ? 1 : secondItem > firstItem ? -1 : 0;
                        if (projectSortByValue.includes('DESC')) {
                            sortRet *= -1;
                        }
                        return sortRet;
                    });
                } else {
                    // Default Sort
                    sortedFeatures = clonedProjects.sort((a, b) => {
                        let firstItem = a[FieldNames.projectFields.name];
                        let secondItem = b[FieldNames.projectFields.name];
                        if (!firstItem) {
                            firstItem = '';
                        }
                        if (!secondItem) {
                            secondItem = '';
                        }
                        return secondItem - firstItem;
                    });
                }
            }

            setSortedProjects(sortedFeatures);
        } catch (e) {
            console.log(e);
        }
    }, [projects, projectSortByValue]);

    useEffect(() => {
        if (isViewingActions && !isViewingProjects) {
            setProjectActionSelection('Actions');
        } else if (!isViewingActions && isViewingProjects) {
            setProjectActionSelection('Projects');
        }
    }, [isViewingActions, isViewingProjects]);

    const handleActionToggle = () => {
        setActionOpen((prevOpen) => !prevOpen);
    };

    const handleActionClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setActionOpen(false);
    };

    const onProjectSelected = (objectId, isPostFire = true) => {
        const entityStringValue = isPostFire ? 'pf' : 'f';
        navigate(`/${entityStringValue}/projects/${objectId}`);
    };

    const projectClicked = (e, objectID, isPostFire) => {
        onProjectSelected(objectID, isPostFire);
    };

    const selectProject = (e, projectObj) => {
        const currentFeature = selectedProjects.filter(
            (x) => x[FieldNames.projectFields.id] === projectObj[FieldNames.projectFields.id]
        );
        if (e.currentTarget.checked) {
            setSelectedProjects((selectedProjects) => [...selectedProjects, projectObj]);
        } else if (!e.currentTarget.checked && currentFeature.length !== 0) {
            const currentFeatureRemoved = selectedProjects.filter(
                (x) => x.selectionID !== projectObj.selectionID
            );
            setSelectedProjects(currentFeatureRemoved);
        }
    };

    const handleSelectAll = (e) => {
        setIsCheckAll(!isCheckAll);
        if (isCheckAll) {
            setSelectedProjects([]);
        } else {
            let featureList = [];
            sortedProjects.forEach((project) => {
                project.selectionID = `${project[FieldNames.featureFields.id]}`;
                featureList.push(project);
            });
            setSelectedProjects(featureList);
        }
    };

    const handleToggleDrawer = () => {
        var wasOpen = listOpen;
        setListOpen(!wasOpen);
    };

    const applyPreferences = () => {
        if (projectListCount !== tempProjectListCount) {
            setProjectListCount(tempProjectListCount);
        }
        if (projectSortByValue !== tempSortByValue) {
            dispatch(setProjectSortByValue(tempSortByValue));
        }
    };

    const handleCountDialogOpen = (event) => {
        setCountPopupAnchorEl(event.currentTarget);
    };

    const handleCountDialogClose = (event) => {
        setCountPopupAnchorEl(null);
    };

    const handleRadioProjectActionChange = () => {
        if (projectActionSelection === 'Actions') {
            handleProjectListRadio();
        } else {
            handleActionListRadio();
        }
    };

    const handleActionListRadio = () => {
        setProjectActionSelection('Actions');
        dispatch(setViewingActions(true));
        dispatch(setViewingProjects(false));
    };

    const handleProjectListRadio = () => {
        setProjectActionSelection('Projects');
        dispatch(setViewingActions(false));
        dispatch(setViewingProjects(true));
    };

    return (
        <Box sx={{ display: 'flex', zIndex: '5!important' }}>
            <CssBaseline />
            <Drawer variant='permanent' open={listOpen} headerheight={headerheight}>
                <DrawerHeader
                    style={{ justifyContent: 'space-between', minHeight: 110, minWidth: 550 }}
                >
                    <IconButton onClick={handleToggleDrawer}>
                        {listOpen ? (
                            <KeyboardDoubleArrowLeftIcon />
                        ) : (
                            <KeyboardDoubleArrowRightIcon />
                        )}
                    </IconButton>
                    <div style={{ width: 485 }}>
                        <Box className={listOpen ? '' : 'hidden'}>
                            <Stack direction={'column'}>
                                <Box>
                                    {isUserPostfire ? (
                                        <RadioGroup
                                            row
                                            aria-labelledby='filter-controlled-radio-buttons-group'
                                            name='filter-controlled-radio-buttons-group'
                                            value={projectActionSelection}
                                            onChange={handleRadioProjectActionChange}
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-evenly',
                                                paddingBottom: 5,
                                            }}
                                        >
                                            <FormControlLabel
                                                value='Actions'
                                                control={<Radio />}
                                                label='Actions'
                                            />
                                            <FormControlLabel
                                                value='Projects'
                                                control={<Radio />}
                                                label='Projects'
                                            />
                                        </RadioGroup>
                                    ) : null}
                                </Box>
                                <Box>
                                    <Grid container spacing={1} alignItems={'center'}>
                                        <Grid size={4}>
                                            <InputLabel
                                                id='sort-by-value-select'
                                                sx={{ display: 'inline' }}
                                            >
                                                {'Sort By: '}
                                            </InputLabel>
                                            <Select
                                                labelId='sort-by-value-select'
                                                sx={{ width: 100, height: 40, marginRight: 2 }}
                                                value={tempSortByValue}
                                                onChange={(event) => {
                                                    setTempSortByValue(event.target.value);
                                                }}
                                            >
                                                <MenuItem value={''} key='0'>
                                                    <br></br>
                                                </MenuItem>
                                                <MenuItem
                                                    value={`${FieldNames.projectFields.name} ASC`}
                                                    key='1'
                                                >
                                                    Name ASC
                                                </MenuItem>
                                                <MenuItem
                                                    value={`${FieldNames.projectFields.name} DESC`}
                                                    key='2'
                                                >
                                                    Name DESC
                                                </MenuItem>
                                                <MenuItem
                                                    value={`${FieldNames.projectFields.fireName} ASC`}
                                                    key='3'
                                                >
                                                    Fire Name ASC
                                                </MenuItem>
                                                <MenuItem
                                                    value={`${FieldNames.projectFields.fireName} DESC`}
                                                    key='4'
                                                >
                                                    Fire Name DESC
                                                </MenuItem>
                                            </Select>
                                        </Grid>
                                        <Grid size={4}>
                                            <Button
                                                disabled
                                                id={countPopupID}
                                                style={{ height: 40 }}
                                                color='primary'
                                                variant='contained'
                                                onClick={handleCountDialogOpen}
                                            >
                                                {'Show'}
                                            </Button>
                                            <Popover
                                                id={countPopupID}
                                                open={countPopupOpen}
                                                anchorEl={countPopupAnchorEl}
                                                onClose={handleCountDialogClose}
                                                anchorOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'center',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'center',
                                                }}
                                            >
                                                <Stack>
                                                    <Typography
                                                        sx={{
                                                            textAlign: 'center',
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        List Card Count(s)
                                                    </Typography>
                                                    <Box
                                                        component='form'
                                                        sx={{ display: 'flex', flexWrap: 'wrap' }}
                                                    >
                                                        <FormControl sx={{ m: 1, minWidth: 120 }}>
                                                            <Typography>Projects:</Typography>
                                                            <Select
                                                                value={tempProjectListCount}
                                                                onChange={(event) => {
                                                                    setTempProjectListCount(
                                                                        event.target.value
                                                                    );
                                                                }}
                                                            >
                                                                <MenuItem value={0}>0</MenuItem>
                                                                <MenuItem value={25}>25</MenuItem>
                                                                <MenuItem value={100}>100</MenuItem>
                                                                <MenuItem value={200}>200</MenuItem>
                                                                <MenuItem value={300}>300</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Box>
                                                    <Button
                                                        style={{ height: 40 }}
                                                        sx={{
                                                            marginBottom: '2.5px',
                                                            marginLeft: '2.5px',
                                                            marginRight: '2.5px',
                                                        }}
                                                        color='primary'
                                                        variant='contained'
                                                        onClick={() => handleCountDialogClose()}
                                                    >
                                                        Close
                                                    </Button>
                                                </Stack>
                                            </Popover>
                                        </Grid>
                                        <Grid size={1}>
                                            <Tooltip
                                                title={
                                                    <div style={{ whiteSpace: 'pre-line' }}>
                                                        These functions are disabled for projects
                                                        while projects functionality is developed.
                                                    </div>
                                                }
                                                enterDelay={100}
                                            >
                                                <HelpIcon fontSize='small' color='primary' />
                                            </Tooltip>
                                        </Grid>
                                        <Grid size={1}>
                                            {(areListCountsDifferent ||
                                                projectSortByValue !== tempSortByValue) && (
                                                <Tooltip
                                                    title='The Apply button must be clicked to apply preferences'
                                                    enterDelay={400}
                                                >
                                                    <WarningAmberIcon
                                                        style={{
                                                            color: ColorConstants.StatusColors
                                                                .Draft,
                                                            fontSize: 40,
                                                            verticalAlign: 'middle',
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Grid>
                                        <Grid size={2.0}>
                                            <Button
                                                style={{ height: 40 }}
                                                color='primary'
                                                variant='contained'
                                                onClick={() => applyPreferences()}
                                            >
                                                Apply
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Stack>
                        </Box>
                    </div>
                </DrawerHeader>
                <Divider />
                <div className='incidentList-panel'>
                    <div
                        className={
                            listOpen ? 'incidentList-buttons' : 'incidentList-buttons hidden'
                        }
                    >
                        <div className='blue-msg'>
                            <div>
                                <Checkbox
                                    type='checkbox'
                                    name='selectAll'
                                    id='selectAll'
                                    onChange={(e) => {
                                        handleSelectAll(e);
                                    }}
                                    checked={isCheckAll}
                                />
                                <InputLabel id='selectAll' sx={{ display: 'inline' }}>
                                    Select All
                                </InputLabel>
                                {/* If we ever need the radios to be stacked again, width=55, marginTop=0, flexDirection=column */}
                                <FormControl
                                    sx={{
                                        width: 150,
                                        height: 40,
                                        marginTop: 1.5,
                                        marginLeft: 2,
                                        display: 'inline-flex',
                                        flexDirection: 'row',
                                    }}
                                >
                                    <RadioGroup
                                        row
                                        value={cardSize}
                                        onChange={(event) => {
                                            setCardSize(event.target.value);
                                        }}
                                    >
                                        <FormControlLabel
                                            sx={{ fontSize: '1rem', height: 15, marginBottom: 1 }}
                                            value={FieldNames.listCardTypes.condensed}
                                            control={
                                                <Radio style={{ padding: 0, display: 'inline' }} />
                                            }
                                            label={FieldNames.listCardTypeNames.condensed}
                                        />
                                        <FormControlLabel
                                            sx={{ fontSize: '1rem', height: 15 }}
                                            value={FieldNames.listCardTypes.detailed}
                                            control={
                                                <Radio style={{ padding: 0, display: 'inline' }} />
                                            }
                                            label={FieldNames.listCardTypeNames.detailed}
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </div>
                            <div>
                                <ButtonGroup
                                    disabled
                                    variant='outlined'
                                    ref={anchorRef}
                                    aria-label='split button'
                                >
                                    <Button color='primary' size='small' style={{ height: '30px' }}>
                                        Map Actions
                                    </Button>
                                    <Button
                                        size='small'
                                        color='primary'
                                        aria-controls={actionOpen ? 'split-button-menu' : undefined}
                                        aria-expanded={actionOpen ? 'true' : undefined}
                                        aria-label='actionsDrowdown'
                                        aria-haspopup='menu'
                                        style={{ height: '30px', minWidth: '20px', width: '30px' }}
                                        onClick={handleActionToggle}
                                    >
                                        <ArrowDropDownIcon />
                                    </Button>
                                </ButtonGroup>
                                <Popper
                                    open={actionOpen}
                                    anchorEl={anchorRef.current}
                                    role={undefined}
                                    transition
                                    disablePortal
                                    disabled
                                    style={{ maxWidth: '300px', zIndex: 5 }}
                                >
                                    {({ TransitionProps, placement }) => (
                                        <Grow
                                            {...TransitionProps}
                                            style={{
                                                transformOrigin:
                                                    placement === 'bottom'
                                                        ? 'center top'
                                                        : 'center bottom',
                                            }}
                                        >
                                            <Paper>
                                                <ClickAwayListener onClickAway={handleActionClose}>
                                                    <MenuList
                                                        id='split-button-menu'
                                                        autoFocusItem
                                                    ></MenuList>
                                                </ClickAwayListener>
                                            </Paper>
                                        </Grow>
                                    )}
                                </Popper>
                            </div>
                        </div>
                    </div>
                    <div className='incident-list' style={{ height: '66vh' }}>
                        <div
                            className='incident-list-inner'
                            style={{ display: 'flex', flexGrow: 1 }}
                        >
                            <AutoSizer>
                                {({ height, width }) => (
                                    <ProjectListCard
                                        height={height}
                                        width={listOpen ? width : 64}
                                        data={sortedProjects}
                                        featureClicked={projectClicked}
                                        selectFeature={selectProject}
                                        selectedFeatures={selectedProjects}
                                        listOpen={listOpen}
                                        cardSize={cardSize}
                                        userInfo={userInfo}
                                    />
                                )}
                            </AutoSizer>
                        </div>
                    </div>
                    <div
                        className={listOpen ? 'incidentList-bottom' : 'incidentList-bottom hidden'}
                    >
                        <MapLegend />
                    </div>
                </div>
            </Drawer>
        </Box>
    );
}
